import { api, throwApiError } from "../../../api/api";

/**
 * Provisões de 13º, férias, 1/3 e FGTS (feat/dp-provisoes). Conferência, como a
 * folha: não substitui o balancete do contador.
 *
 * `Decimal` do Prisma chega como STRING (BUG-R05): converter com `Number()` antes
 * de formatar.
 */

export type DpProvisionCode =
  | "PROV_13"
  | "PROV_13_ABATIDO"
  | "PROV_FERIAS_VENCIDAS"
  | "PROV_FERIAS_PROPORCIONAIS"
  | "PROV_TERCO"
  | "PROV_FGTS";

export type DpProvisionMeta = {
  fullName: string;
  cpf: string | null;
  registrationNumber: string | null;
  costCenter: { code: string; name: string } | null;
  store: string | null;
  costCenterId: string | null;
  storeId: string | null;
  /** AAAA-MM-DD. */
  admissionDate: string;
};

export type DpProvisionAmounts = {
  thirteenthAmount: string;
  vacationAmount: string;
  vacationThirdAmount: string;
  fgtsAmount: string;
  totalAmount: string;
};

export type DpVacationDays = { overdue: number; doubled: number; proportional: number };

export type DpProvisionRow = DpProvisionAmounts & {
  contractId: string;
  employeeId: string;
  meta: DpProvisionMeta;
  thirteenthAvos: number;
  vacationDays: DpVacationDays;
  pending: string[];
  warnings: string[];
  /** Total da foto do mês anterior (nulo = sem foto). */
  previousTotal: string | null;
};

export type DpProvisionCostCenterTotal = DpProvisionAmounts & {
  costCenterId: string | null;
  code: string | null;
  name: string;
  count: number;
  previousTotal: string | null;
};

export type DpProvisionOverview = {
  asOf: string;
  employerId: string;
  rows: DpProvisionRow[];
  totals: DpProvisionAmounts & { count: number; pendingCount: number };
  byCostCenter: DpProvisionCostCenterTotal[];
  /** Foto do mês anterior ao da data: `totalAmount` nulo = sem foto. */
  previous: { year: number; month: number; count: number; totalAmount: string | null };
  /**
   * Sugestão de quanto provisionar no mês seguinte ao da data, pelo quadro atual: um mês
   * cheio de 13º, férias, 1/3 e FGTS. `excludedCount` = fora por pendência ou desligamento no mês.
   */
  nextMonth: DpProvisionAmounts & { year: number; month: number; count: number; excludedCount: number };
};

export type DpProvisionLine = {
  code: DpProvisionCode;
  name: string;
  reference: string | null;
  amount: string;
  memo: string;
};

export type DpProvisionHistoryItem = DpProvisionAmounts & {
  year: number;
  month: number;
  asOf: string;
  pending: string[];
  createdAt: string;
};

export type DpProvisionDetail = DpProvisionAmounts & {
  asOf: string;
  contractId: string;
  employeeId: string;
  meta: DpProvisionMeta;
  lines: DpProvisionLine[];
  summary: {
    thirteenthAvos: number;
    base13: string | null;
    baseVacation: string | null;
    vacationDays: DpVacationDays;
    fgtsPct: string;
  };
  pending: string[];
  warnings: string[];
  history: DpProvisionHistoryItem[];
};

export type DpProvisionHistoryMonth = DpProvisionAmounts & {
  year: number;
  month: number;
  count: number;
  pendingCount: number;
  generatedAt: string;
  byCostCenter: Array<DpProvisionAmounts & { costCenterId: string | null; code: string | null; name: string; count: number }>;
};

export type DpProvisionHistory = { employerId: string; from: string; to: string; months: DpProvisionHistoryMonth[] };

export type ProvisionsQuery = { asOf?: string; employerId?: string; costCenterId?: string; storeId?: string };

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};
const clean = (params: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""));

async function call<T>(fallback: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

export const getProvisionsApi = (token: string, query: ProvisionsQuery) =>
  call<DpProvisionOverview>("Erro ao carregar as provisões.", () =>
    api.get("/dp/provisions", { ...auth(token), params: clean(query) })
  );

/** CSV como blob: um link direto não mandaria o token. */
export const exportProvisionsApi = (token: string, query: ProvisionsQuery) =>
  call<Blob>("Erro ao exportar o CSV.", () =>
    api.get("/dp/provisions/export", { ...auth(token), params: clean(query), responseType: "blob" })
  );

export const getProvisionHistoryApi = (token: string, query: { employerId?: string; from?: string; to?: string }) =>
  call<DpProvisionHistory>("Erro ao carregar o histórico das provisões.", () =>
    api.get("/dp/provisions/history", { ...auth(token), params: clean(query) })
  );

export const createProvisionSnapshotApi = (token: string, payload: { employerId: string; year: number; month: number }) =>
  // financeiroWarnings: a sugestão do mês seguinte vai para a DRE pela ponte (ADR 0036); erro lá vira aviso.
  call<{ year: number; month: number; count: number; pendingCount: number; financeiroWarnings?: string[] }>("Erro ao gerar a foto das provisões.", () =>
    api.post("/dp/provisions/snapshots", payload, auth(token))
  );

export const getContractProvisionApi = (token: string, contractId: string, asOf?: string) =>
  call<DpProvisionDetail>("Erro ao carregar a provisão.", () =>
    api.get(`/dp/contracts/${contractId}/provisions`, { ...auth(token), params: clean({ asOf }) })
  );

/** Hoje (data local do navegador) em AAAA-MM-DD. */
export const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/** Variação contra a foto anterior: valor e percentual (nulo sem foto). */
export const variationOf = (current: string | number, previous: string | number | null) => {
  if (previous === null) return null;
  const diff = Number(current) - Number(previous);
  const pct = Number(previous) === 0 ? null : (diff / Number(previous)) * 100;
  return { diff, pct };
};
